import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@ibsys/kit';
import { RenessansDialogResultsResponse } from '@/entities/renessans-dashboards';
import { formatPercent } from '@/shared/lib/utils/string';

type Props = {
  data: RenessansDialogResultsResponse | undefined;
  isLoading: boolean;
};

/**
 * Виджет 5 «Результаты диалога»: набор блоков по распределению значений
 * `policy[*].terminationStatus`. Каждый блок — две строки: верхняя `value`
 * (мелкий шрифт), нижняя — процент в формате «XX.X%» (крупный шрифт).
 * Порядок блоков повторяет порядок `distribution` из ответа (бэкенд сортирует
 * по убыванию `percent`). Локального пересчёта процента нет — значение уже
 * округлено бэкендом до одного знака. При пустом `distribution` отрисовывается
 * фоллбек «Нет данных». Pie chart, тултипы, легенда и фильтр не используются.
 */
export const DialogResultsWidget: FC<Props> = ({ data, isLoading }) => {
  const { t } = useTranslation('renessansDashboard');

  const distribution = data?.distribution ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle>{t('dialogResults.title')}</CardTitle>
        {isLoading && <Spinner className="size-4" />}
      </CardHeader>
      <CardContent>
        {distribution.length === 0 && <div className="text-muted-foreground text-sm">{t('noData')}</div>}
        {distribution.length > 0 && (
          <div className="flex flex-wrap gap-6">
            {distribution.map((item) => (
              <div key={item.value} className="flex flex-col gap-1">
                <span className="text-muted-foreground text-sm">{item.value}</span>
                <span className="text-foreground text-3xl font-semibold tabular-nums">
                  {formatPercent(item.percent)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
